import { ITopicEntity } from '@1js/cortex-topics-types';
import { moveToFulfilled, remove, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  CortexTopicsReceivedAction,
  OfficeTopicLoadAuthorizedFulfilledAction,
} from '../../domains/cortexTopics/actions.types';
import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import { GroupPopularTopicsLoadFulfilledAction } from '../../domains/groupPopularTopicsLoad/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserMomentMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import {
  SuggestedTopicsLoadFulfilledAction,
  SuggestedTopicsLoadMoreFulfilledAction,
} from '../../domains/suggestedTopics/actions.types';
import { ThreadListLoadFulfilledAction } from '../../domains/threadListLoad/actions.types';
import {
  ThreadLoadFulfilledAction,
  ThreadLoadNewerRepliesFulfilledAction,
  ThreadLoadOlderRepliesFulfilledAction,
} from '../../domains/threadLoad/actions.types';
import { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import { TopicHeaderLoadAction, TopicHeaderLoadFulfilledAction } from '../../domains/topicHeaderLoad/actions.types';
import { TopicSidebarLoadFulfilledAction } from '../../domains/topicSidebars/actions.types';
import {
  AddThreadTopicFulfilledAction,
  CreateCuratedTopicFulfilledAction,
  RemoveCuratedTopicFulfilledAction,
  TopicCreateFulfilledAction,
  TopicCurateFulfilledAction,
  TopicEditFulfilledAction,
  TopicFetchAction,
  TopicFetchByIdAction,
} from '../../domains/topics/actions.types';
import {
  TopicsCuratedLoadFulfilledAction,
  TopicsCuratedLoadMoreFulfilledAction,
} from '../../domains/topicsCurated/actions.types';
import {
  TopicsFollowedLoadFulfilledAction,
  TopicsFollowedLoadMoreFulfilledAction,
} from '../../domains/topicsFollowedLoad/actions.types';
import {
  TrendingTopicsLoadFulfilledAction,
  TrendingTopicsLoadMoreFulfilledAction,
} from '../../domains/trendingTopicsLoad/actions.types';
import { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';

import { Topic, Topics } from './types';

export type TopicAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | GroupPopularTopicsLoadFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | ThreadLoadFulfilledAction
  | ThreadLoadNewerRepliesFulfilledAction
  | ThreadLoadOlderRepliesFulfilledAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | ThreadListLoadFulfilledAction
  | OfficeTopicLoadAuthorizedFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserMomentMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishNetworkQuestionMessageFulfilledAction
  | TypeaheadFulfilledAction
  | TopicFetchAction
  | TopicFetchByIdAction
  | TopicHeaderLoadAction
  | TopicsFollowedLoadFulfilledAction
  | TopicsFollowedLoadMoreFulfilledAction
  | TrendingTopicsLoadFulfilledAction
  | TrendingTopicsLoadMoreFulfilledAction
  | AddThreadTopicFulfilledAction
  | TopicHeaderLoadFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | TopicCreateFulfilledAction
  | TopicCurateFulfilledAction
  | RemoveCuratedTopicFulfilledAction
  | TopicsCuratedLoadFulfilledAction
  | TopicsCuratedLoadMoreFulfilledAction
  | CreateCuratedTopicFulfilledAction
  | SuggestedTopicsLoadFulfilledAction
  | SuggestedTopicsLoadMoreFulfilledAction
  | TopicEditFulfilledAction
  | TopicSidebarLoadFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | CortexTopicsReceivedAction;

const initialState: Topics = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithFulfilledTopics = (state: Topics, newTopics: Record<Topic['id'], Topic> | undefined) => {
  if (!newTopics) {
    return state;
  }

  return Object.keys(newTopics).reduce((newState, topicId) => {
    const existingTopic = newState.fulfilled.byId[topicId];
    const payloadTopic: Topic = newTopics[topicId];
    const existingTopicHasName = existingTopic && existingTopic.name != null;
    const existingTopicNeedsDescription =
      existingTopic && !existingTopic.description && payloadTopic.description != null;
    const existingTopicNeedsShortDescription =
      existingTopic && !existingTopic.shortDescription && payloadTopic.shortDescription != null;
    const payloadTopicIsMissingAuthToken = !payloadTopic.officeTopicAuthorizationToken;

    if (
      existingTopicHasName &&
      payloadTopicIsMissingAuthToken &&
      !existingTopicNeedsDescription &&
      !existingTopicNeedsShortDescription
    ) {
      return newState;
    }

    return moveToFulfilled(newState, {
      [topicId]: payloadTopic,
    });
  }, state);
};

const updateTopicsWithName = (state: Topics, action: CortexTopicsReceivedAction) => {
  const fulfilledTopics = state.fulfilled.byId;
  const officeTopicsPayload = action.payload;

  type OfficeTopicsById = Record<ITopicEntity['Id'], ITopicEntity>;
  type TopicsByOfficeTopicId = Record<ITopicEntity['Id'], ITopicEntity>;

  // Cortex topics can mutate as they are merged and updated by the SDK.  This means
  // that the SDK will return a desired topic based on the client request and return
  // that topic with a different officeTopicId.  This is a temporary fix until the
  // SDK can update its API.

  const officeTopicsByAncestorId: OfficeTopicsById = officeTopicsPayload.reduce((topicsByAncestorId, topic) => {
    if (!topic.AncestorIds || !topic.AncestorIds.length) {
      return topicsByAncestorId;
    }

    const topicByAncestorIds = topic.AncestorIds.reduce(
      (topicByAncestorIds, ancestorId) => ({ ...topicByAncestorIds, [ancestorId]: topic }),
      {}
    );

    return { ...topicsByAncestorId, ...topicByAncestorIds };
  }, {});

  const officeTopicsPayloadById: OfficeTopicsById = officeTopicsPayload.reduce(
    (officeTopicsById, topic) => ({ ...officeTopicsById, [topic.Id]: topic }),
    {}
  );

  const topicsByOfficeTopicId: TopicsByOfficeTopicId = Object.values(fulfilledTopics).reduce(
    (fulfilledTopics, topic) =>
      topic.officeTopicId ? { ...fulfilledTopics, [topic.officeTopicId]: topic } : fulfilledTopics,
    {}
  );

  return Object.keys(fulfilledTopics).reduce((newState, topicId) => {
    const { name, officeTopicId } = fulfilledTopics[topicId];

    if (!officeTopicId) {
      return newState;
    }

    const officeTopicAncestor = officeTopicsByAncestorId[officeTopicId];
    const officeTopicPayload = officeTopicsPayloadById[officeTopicId];

    if (officeTopicPayload && !name) {
      return update(newState, topicId, {
        name: officeTopicPayload.DisplayName,
      });
    }

    if (officeTopicAncestor && !topicsByOfficeTopicId[officeTopicAncestor.Id]) {
      return update(newState, topicId, {
        name: officeTopicAncestor.DisplayName,
        officeTopicId: officeTopicAncestor.Id,
      });
    }

    if (!name) {
      return remove(newState, [topicId]);
    }

    return newState;
  }, state);
};

// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<Topics, TopicAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'GROUP_POPULAR_TOPICS.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SEARCH.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.LOAD_NEWER_REPLIES_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.LOAD_OLDER_REPLIES_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD_LIST.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_CURATED.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_CURATED.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SUGGESTED_TOPICS.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SUGGESTED_TOPICS.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC_HEADER.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_FOLLOWED.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TRENDING_TOPICS.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TRENDING_TOPICS.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD_TOPIC.ADD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.CREATE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.CREATE_AND_CURATE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'CURATED_TOPIC.REMOVE_FULFILLED':
      return remove(state, [action.payload.topicId]);
    case 'TOPIC.EDIT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.CURATE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'OFFICE_TOPIC.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.FETCH_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.FETCH_BY_ID_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC_SIDEBAR.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'OFFICE_TOPICS.CORTEX_TOPICS_RECEIVED':
      return updateTopicsWithName(state, action);
    default:
      return state;
  }
};

export default reducer;
